import { LoginPage } from "@presentation/pages";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Sidebar } from "@presentation/components/layout/sidebar/Sidebar"; // import {Sidebar} from '@presentation/components'; for some reason importing is nto workign this way with this component only...
import { useUserManagementStore } from "@presentation/stores/UserManagmentStore";
import { NotificationProvider } from "@presentation/components/notifications";
import React, { useEffect, useRef, useState } from "react";
import "@utils/stringLocalization/i18n";
import OidcCallback from "./components/auth/OidcCallback";
import { useInjection } from "./components/dependencyInjection/useInjection";
import { AuthService } from "@infrastructure/services";
import { FullscreenLoader } from "@presentation/components/notifications";
import { logOutInProgress, signInProgress } from "@presentation/components/helpers/constants";
import { InactivityAutoSignOut } from "./components/auth";
import { UploadProgressIndicator } from "@presentation/components";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { NewSoftwareVersionsNotification } from "@presentation/components/softwareVersions/NewSoftwareVersionsNotification";
import { appRoutes } from "@presentation/AppRoutes";
import { canPerformProjectAction, canPerformTeamAction, selectIsActivePermissionPending, usePermissionStore } from "@presentation/stores/PermissionStore";
import { useWorkspaceHydration, type WorkspaceAuthStatus } from "@presentation/components/helpers/useWorkspaceHydration";
import { PermissionGatedRoute } from "@presentation/components/permission/PermissionGatedRoute";
import type { RouteItemProps } from "@presentation/common/RouteItemProps";

const renderableRoutes = appRoutes.filter((route) => route.route && route.element);
const DevelopmentRbacPersonaPanel = React.lazy(async () => {
  const module = await import("./components/development/RbacPersonaPanel");
  return { default: module.RbacPersonaPanel };
});

const App: React.FC = () => {
  const [checkedSession, setCheckedSession] = useState(false);
  const username = useUserManagementStore((state) => state.userSession?.username);
  // Tri-state, not a boolean: until the session check completes we do not know
  // whether anyone is signed in, and treating that as "signed out" would clear
  // the persisted workspace context before restoration could use it.
  const workspaceAuthStatus: WorkspaceAuthStatus = checkedSession ? (username ? "authenticated" : "unauthenticated") : "unknown";
  useWorkspaceHydration(workspaceAuthStatus, username ?? null);
  const authService = useRef(useInjection(AuthService)).current;
  const isPermissionPending = usePermissionStore(selectIsActivePermissionPending);
  const canRoute = (route: RouteItemProps): boolean => {
    if (!route.requiredPermission) return true;
    if (isPermissionPending) return false;
    const state = usePermissionStore.getState();
    return route.requiredPermission.scope === "team" ? canPerformTeamAction(route.requiredPermission.action)(state) : canPerformProjectAction(route.requiredPermission.action)(state);
  };
  const sidebarRoutes = appRoutes.filter((route) => {
    if (route.hideFromSidebar) return false;
    return route.separator ? true : canRoute(route);
  });

  useEffect(() => {
    const wasLoggedOut = sessionStorage.getItem(logOutInProgress) === "true";
    const wasSignInStarted = sessionStorage.getItem(signInProgress) === "true";
    if (wasLoggedOut || wasSignInStarted) {
      console.info("Skipping session restore");
      sessionStorage.removeItem(logOutInProgress); // Clear the flag so it doesn't persist
      setCheckedSession(true);
      return; // Skip session restore
    }
    console.info("Attempting to restore session");
    const tryRestoreSession = async () => {
      const session = useUserManagementStore.getState().userSession;
      const token = session?.accessToken ?? null;
      if (session.username) console.info("Token is present");

      if (token && !authService.isTokenExpired(token)) {
        console.info("Session restored from memory");
        setCheckedSession(true);
        return;
      }

      try {
        console.info("Attempting to refresh access token...");
        const refreshedToken = await authService.refreshAccessToken();

        if (refreshedToken) {
          console.info("Access token refreshed successfully");
          setCheckedSession(true);
          return;
        }

        throw new Error("No token returned from refresh");
      } catch (refreshError) {
        console.warn("Token refresh failed, falling back to sign-in:", refreshError);

        try {
          await authService.signIn();
          console.info("Session restored via sign-in");
        } catch (signInError) {
          console.warn("Sign-in failed:", signInError);
          useUserManagementStore.getState().clear();
        } finally {
          setCheckedSession(true);
        }
      }
    };

    tryRestoreSession();
  }, []);

  if (!checkedSession) {
    return <FullscreenLoader />;
  }

  return (
    <NotificationProvider>
      {/* Tooltip provider: must be placed in App.tsx to enable tooltips across the app.
      It automatically shows a tooltip for any child element with a 'title' attribute.
      'parentTitle={true}' ensures the tooltip also works when the 'title' is on a parent element,
      which is especially useful for icon-only buttons. */}
      <Tooltip position="top" parentTitle={true} />
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        {username ? (
          <>
            {checkedSession && <InactivityAutoSignOut />}
            <NewSoftwareVersionsNotification key={username} username={username} />
            <Sidebar routes={sidebarRoutes}>
              <Routes>
                {renderableRoutes.map((route, index) => (
                  <Route key={index} path={route.route} element={<PermissionGatedRoute route={route} />} />
                ))}
              </Routes>
              <UploadProgressIndicator />
              {import.meta.env.DEV && import.meta.env.VITE_ENABLE_RBAC_DEV_TOOLS === "true" && (
                <React.Suspense fallback={null}>
                  <DevelopmentRbacPersonaPanel />
                </React.Suspense>
              )}
            </Sidebar>
          </>
        ) : (
          <Routes>
            <Route path="*" element={<LoginPage />} />
            <Route path="/auth/callback" element={<OidcCallback />} />
          </Routes>
        )}
      </BrowserRouter>
    </NotificationProvider>
  );
};

export default App;
